import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

type Step = 'email' | 'code';

export default function Login() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('admin@chin.vn');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // If already logged in, redirect to dashboard
  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  if (user) {
    return null;
  }

  const sendCode = async () => {
    setError('');
    if (!email) {
      setError('Vui lòng nhập email của bạn.');
      return;
    }

    setLoading(true);
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
      },
    });
    setLoading(false);

    if (otpError) {
      setError(
        otpError.message.includes('User not found') ||
          otpError.message.includes('not found') ||
          otpError.status === 400
          ? 'Email này chưa được cấp quyền truy cập.'
          : `Gửi mã thất bại: ${otpError.message}`
      );
      return;
    }

    setStep('code');
    setCooldown(60);
  };

  const verifyCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!code || code.trim().length < 6) {
      setError('Vui lòng nhập đầy đủ mã đăng nhập.');
      return;
    }

    setLoading(true);
    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      email,
      token: code.trim(),
      type: 'email',
    });
    setLoading(false);

    if (verifyError) {
      setError(
        verifyError.message.includes('expired')
          ? 'Mã đã hết hạn. Vui lòng gửi lại mã mới.'
          : verifyError.message.includes('invalid') ||
              verifyError.message.includes('Invalid')
            ? 'Mã không đúng. Vui lòng kiểm tra lại.'
            : `Xác nhận thất bại: ${verifyError.message}`
      );
      return;
    }

    if (data.session) {
      navigate('/', { replace: true });
    } else {
      setError('Không thể hoàn tất đăng nhập. Vui lòng thử lại.');
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background-50 px-4">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-primary-100/60 blur-3xl sm:h-96 sm:w-96" />
        <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-accent-100/60 blur-3xl sm:h-96 sm:w-96" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-2xl border-2 border-orange-500 bg-background-50 p-6 sm:p-8 md:p-10">
          <div className="mb-6 text-center sm:mb-8">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-500 text-background-50 sm:mb-4 sm:h-14 sm:w-14">
              <i className="ri-book-2-line text-xl sm:text-2xl" />
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-950 sm:text-2xl">
              Note System
            </h1>
            <p className="mt-1.5 text-sm text-foreground-500 sm:mt-2">
              Đăng nhập bằng mã gửi qua email
            </p>
          </div>

          {/* Email step */}
          {step === 'email' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendCode();
              }}
              className="space-y-4 sm:space-y-5"
            >
              <p className="text-center text-sm text-foreground-500">
                Nhập email để nhận mã đăng nhập, không cần mật khẩu.
              </p>

              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-sm font-medium text-foreground-700"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@chin.vn"
                  className="w-full rounded-lg border border-background-200 bg-background-100/60 px-3 py-3 text-base text-foreground-900 outline-none transition placeholder:text-foreground-400 hover:bg-background-100 focus:border-primary-500 focus:bg-background-50 focus:ring-2 focus:ring-primary-200 sm:py-2.5 sm:text-sm"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
                  <i className="ri-error-warning-line mt-0.5 text-base" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3.5 text-base font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:py-2.5 sm:text-sm"
              >
                {loading ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-background-50 border-t-transparent sm:h-4 sm:w-4" />
                    Đang gửi mã...
                  </>
                ) : (
                  'Gửi mã đăng nhập'
                )}
              </button>
            </form>
          )}

          {/* Code verification step */}
          {step === 'code' && (
            <form onSubmit={verifyCode} className="space-y-4 sm:space-y-5">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-mail-open-line text-xl" />
                </div>
                <p className="text-sm text-foreground-600">
                  Mã đăng nhập đã được gửi đến
                </p>
                <p className="text-sm font-semibold text-foreground-900">{email}</p>
              </div>

              <div>
                <label
                  htmlFor="code"
                  className="mb-1.5 block text-center text-sm font-medium text-foreground-700"
                >
                  Nhập mã xác nhận
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="000000"
                  className="w-full rounded-lg border border-background-200 bg-background-100/60 px-3 py-3 text-center text-2xl font-semibold tracking-[0.3em] text-foreground-900 outline-none transition placeholder:text-foreground-300 hover:bg-background-100 focus:border-primary-500 focus:bg-background-50 focus:ring-2 focus:ring-primary-200 sm:text-3xl"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
                  <i className="ri-error-warning-line mt-0.5 text-base" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3.5 text-base font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:py-2.5 sm:text-sm"
              >
                {loading ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-background-50 border-t-transparent sm:h-4 sm:w-4" />
                    Đang xác nhận...
                  </>
                ) : (
                  'Xác nhận & đăng nhập'
                )}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setError('');
                  }}
                  className="whitespace-nowrap text-sm font-medium text-foreground-500 transition hover:text-foreground-700"
                >
                  Dùng email khác
                </button>
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={cooldown > 0 || loading}
                  className="whitespace-nowrap text-sm font-medium text-primary-600 transition hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {cooldown > 0 ? `Gửi lại mã (${cooldown}s)` : 'Gửi lại mã'}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-foreground-400 sm:mt-6">
          Hệ thống chỉ dành cho quản trị viên được cấp quyền.
        </p>
      </div>
    </div>
  );
}